/* eslint-disable check-file/filename-blocklist */
import { createElement } from 'react';
import type { IntlShape } from 'react-intl';

import type { ADNode } from '@atlaskit/editor-common/utils/validator';
import type { EditorActions } from '@atlaskit/editor-core/actions';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type { ExtensionModule } from '@atlaskit/editor-common/extensions/extension-manifest';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { ExtensionPlugin } from '@atlaskit/editor-plugins/extension';
import AppIcon from '@atlaskit/icon/core/app';
import AttachmentIcon from '@atlaskit/icon/core/attachment';
import RoadmapIcon from '@atlaskit/icon/core/roadmap';
import TextIndentRightIcon from '@atlaskit/icon/core/text-indent-right';
import JiraIcon from '@atlaskit/icon-lab/core/jira';

import { isExperimentEnabled } from '@confluence/feature-experiments';
import { getMacroId } from '@confluence/macro-tracker/entry-points/getExtensionKey.ts';
import { cfetch } from '@confluence/network';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import type { ExtensionNodeType } from '../../extensions-common/extensionNodeTypes.ts';
import { isBodiedExtension } from '../../extensions-common/extensionNodeTypes.ts';
import { allowedMacrosForInsertMenu } from '../../extensionConstants.ts';

import type { MacroConfig } from './extension-provider-types.ts';
import type {
	LegacyMacroManifest,
	ConfluencePageContext,
	OnCompleteCallbackFnType,
} from './extensionTypes.ts';
import { safeGetMacroName } from './manifest-helpers.tsx';
// eslint-disable-next-line import/no-cycle
import { getQuickInsertAction } from './quick-insert-action.ts';
import { getIconComponent } from './MacroComponents.tsx';

const pageIdRegex = /^[0-9]+$/;
const mediaQuickInsertMacros = new Set(['roadmap', 'profile-picture', 'attachments']);
const appIconQuickInsertMacros = new Set([
	'include',
	'toc-zone',
	'opsgenie-incident-timeline-prod-jsm-issue-activity-macro',
	'opsgenie-incident-timeline-eu-jsm-issue-activity-macro',
	'opsgenie-incident-timeline-prod-macro',
	'opsgenie-incident-timeline-eu-macro',
	'viewpdf',
	'widget',
	'sharelinks-urlmacro',
	'jirachart',
]);
const otherQuickInsertMacros = new Set([
	'excerpt',
	'excerpt-include',
	'include',
	'jirachart',
	'jira',
	'portfolioforjiraplan',
	'opsgenie-incident-timeline-prod-jsm-issue-activity-macro',
	'opsgenie-incident-timeline-eu-jsm-issue-activity-macro',
	'opsgenie-incident-timeline-eu-macro',
	'opsgenie-incident-timeline-prod-macro',
	'sharelinks-urlmacro',
	'toc-zone',
	'viewpdf',
	'viewxls',
	'widget',
]);
const otherQuickInsertMacroPriorities: Readonly<Record<string, number>> = {
	excerpt: -4400,
	'excerpt-include': -4300,
	include: -4200,
	'toc-zone': -4100,
	jira: -4000,
	portfolioforjiraplan: -3900,
	'opsgenie-incident-timeline-prod-jsm-issue-activity-macro': -3800,
	'opsgenie-incident-timeline-eu-jsm-issue-activity-macro': -3700,
	'opsgenie-incident-timeline-prod-macro': -3600,
	'opsgenie-incident-timeline-eu-macro': -3500,
	viewxls: -3400,
	viewpdf: -3300,
	widget: -3200,
	'sharelinks-urlmacro': -3100,
	jirachart: -1900,
};
const gadgetQuickInsertMacroPriorities: Readonly<Record<string, number>> = {
	'com.atlassian.streams.streams-jira-plugin:activitystream-gadget': -3000,
	'com.pyxis.greenhopper.jira:greenhopper-gadget-rapid-view': -2900,
	'com.atlassian.jira.gadgets:assigned-to-me-gadget': -2800,
	'com.atlassian.jira.gadgets:average-age-chart-gadget': -2700,
	'com.atlassian.jira.ext.charting:numberoftimesinstatus-gadget': -2600,
	'com.atlassian.jira.ext.charting:timeinstatus-gadget': -2500,
	'com.atlassian.jira.gadgets:created-vs-resolved-issues-chart-gadget': -2400,
	'com.pyxis.greenhopper.jira:greenhopper-gadget-sprint-days-remaining': -2300,
	'com.atlassian.jira.gadgets:filter-results-gadget': -2200,
	'com.atlassian.jira.gadgets:heat-map-gadget': -2100,
	'com.atlassian.jira.gadgets:in-progress-gadget': -2000,
	'com.atlassian.jira.ext.calendar:issuescalendar-gadget': -1800,
	'com.atlassian.jira.gadgets:road-map-gadget': -1700,
	'com.atlassian.jira.gadgets:labels-gadget': -1600,
	'com.atlassian.jira.gadgets:pie-chart-gadget': -1500,
	'com.atlassian.jira.gadgets:quicklinks-gadget': -1400,
	'com.atlassian.jira.gadgets:recently-created-chart-gadget': -1300,
	'com.atlassian.jira.gadgets:resolution-time-gadget': -1200,
	'com.pyxis.greenhopper.jira:greenhopper-gadget-sprint-burndown': -1100,
	'com.pyxis.greenhopper.jira:greenhopper-gadget-sprint-health': -1000,
	'com.atlassian.jira.gadgets:favourite-filters-gadget': -900,
	'com.atlassian.jira.gadgets:time-since-chart-gadget': -800,
	'com.atlassian.jira.ext.charting:firstresponse-gadget': -700,
	'com.atlassian.jira.gadgets:two-dimensional-stats-gadget': -600,
	'com.pyxis.greenhopper.jira:greenhopper-gadget-version-report': -500,
	'com.atlassian.jira.gadgets:voted-gadget': -400,
	'com.atlassian.jira.gadgets:watched-gadget': -300,
	'com.atlassian.jira.gadgets:stats-gadget': -200,
	'com.atlassian.jira.ext.charting:workloadpie-gadget': -100,
};
const mediaQuickInsertMacroPriorities: Readonly<Record<string, number>> = {
	roadmap: -400,
	'profile-picture': -300,
	attachments: -200,
};
const gadgetModulePathPrefix = '/rest/gadgets/1.0/g/';

const getGadgetQuickInsertPriority = (macro: LegacyMacroManifest): number | undefined => {
	const gadgetUrl = macro.formDetails.parameters.find(
		(parameter) => parameter.name === 'url',
	)?.defaultValue;
	const gadgetModulePath = gadgetUrl?.split(gadgetModulePathPrefix)[1];
	const gadgetModuleKey = gadgetModulePath?.split('/')[0];

	return gadgetModuleKey ? gadgetQuickInsertMacroPriorities[gadgetModuleKey] : undefined;
};

const getQuickInsertIcon = (macro: LegacyMacroManifest, macroName: string) => {
	if (isExperimentEnabled('platform_editor_slash_command')) {
		if (macro.macroName === 'gadget' || appIconQuickInsertMacros.has(macroName)) {
			return () => createElement(AppIcon, { label: '' });
		}

		switch (macroName) {
			case 'attachments':
				return () => createElement(AttachmentIcon, { label: '' });
			case 'roadmap':
				return () => createElement(RoadmapIcon, { label: '' });
			case 'excerpt':
			case 'excerpt-include':
				return () => createElement(TextIndentRightIcon, { label: '' });
			case 'portfolioforjiraplan':
				return () => createElement(JiraIcon, { label: '' });
		}
	}

	return () => getIconComponent(macro);
};

export const isPageId = (id: string): boolean => pageIdRegex.test(id);

// Temporary solution until we have `extensionAPI.doc.getSelectedNode` @see ED-13045
export const getSelectedBodiedExtensionNode = (
	editorActions?: EditorActions,
): ADNode | undefined => {
	const editorView = editorActions?._privateGetEditorView();
	if (editorView) {
		let maybeNode = (editorView.state.selection as any).node;
		if (!maybeNode || !isBodiedExtension(maybeNode.type.name)) {
			const { $head } = editorView.state.selection;
			// If current selection isn't bodied extension traverse upward
			for (let i = $head.depth; i > 0; i--) {
				const node = $head.node(i);
				if (isBodiedExtension(node.type.name as ExtensionNodeType)) {
					maybeNode = node;
				}
			}
		}
		return maybeNode?.toJSON() as ADNode;
	}
};

export const isValueSet = (value: any): boolean =>
	(value === 0 || value === false || !!value) && (!Array.isArray(value) || value.length > 0);

/**
 * True when a Team Calendar macro instance has a configured calendar id.
 *
 * Handles both the wrapped ({ value }) and bare macroParams shapes, and
 * type-guards against non-string runtime values (macroParams values are typed
 * `any` and are not guaranteed to be strings) before calling `.trim()`.
 */
export const hasConfiguredTeamCalendar = (
	macroParams?: Record<string, { value?: unknown } | string | undefined>,
): boolean => {
	const id = macroParams?.id;
	const value = typeof id === 'object' && id !== null && 'value' in id ? id.value : id;

	return typeof value === 'string' && value.trim() !== '';
};

export const createQuickInsertModule = (
	macro: LegacyMacroManifest,
	pageContext: ConfluencePageContext,
	openMacroBrowserForInitialConfiguration: any,
	editorAPI: PublicPluginAPI<[ExtensionPlugin]> | undefined,
	intl: IntlShape,
	macroBrowserConfig?: MacroConfig,
	editorActions?: EditorActions,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	onCompleteCallback?: OnCompleteCallbackFnType,
	isLivePage?: boolean,
): ExtensionModule[] => {
	// Hidden macros don't appear in the slash menu
	// but still need to be defined in a manifest so old pages can show them
	if (macro.hidden) {
		return [];
	}

	const macroName = safeGetMacroName(macro);
	const isOtherQuickInsertMacro =
		macro.macroName === 'gadget' || otherQuickInsertMacros.has(macroName);
	const category = !isExperimentEnabled('platform_editor_slash_command')
		? undefined
		: isOtherQuickInsertMacro
			? 'other'
			: mediaQuickInsertMacros.has(macroName)
				? 'media'
				: undefined;
	const priority = !isExperimentEnabled('platform_editor_slash_command')
		? undefined
		: isOtherQuickInsertMacro
			? macro.macroName === 'gadget'
				? getGadgetQuickInsertPriority(macro)
				: otherQuickInsertMacroPriorities[macroName]
			: mediaQuickInsertMacroPriorities[macroName];
	const icon = getQuickInsertIcon(macro, macroName);

	try {
		const action = getQuickInsertAction(
			macro,
			pageContext,
			openMacroBrowserForInitialConfiguration,
			editorAPI,
			intl,
			macroBrowserConfig,
			editorActions,
			createAnalyticsEvent,
			onCompleteCallback,
			isLivePage,
		);

		return [
			{
				key: macroName,
				title: macro.title,
				description: macro.description,
				keywords: [macro.macroName, ...macro.aliases],
				...(category ? { category } : {}),
				...(priority !== undefined ? { priority } : {}),
				categories: macro.categories,
				icon: async () => icon,
				featured:
					pageContext.features.elementBrowser && allowedMacrosForInsertMenu.includes(macroName),
				action,
			},
		];
	} catch (e) {
		// eslint-disable-next-line no-console
		console.error(e);
		return [];
	}
};

// Extract App Key from moduleKey for connect app
// com.atlassian.plugins.atlassian-connect-plugin:xhtml__cc-eco-test-connect-multi-bodied-extension__cc-eco-test-multi-bodied-extension
// ====>
// cc-eco-test-connect-multi-bodied-extension
export const getConnectAppKey = (macroKey?: string | null): string => {
	if (!macroKey || !macroKey.startsWith('com.atlassian.plugins.atlassian-connect-plugin')) {
		return '';
	}

	const chunks = macroKey.split('__');
	if (chunks.length !== 3) {
		return '';
	}

	return chunks[1];
};

export const insertMacroPlaceholder = (node: any, placeholderText: string): void => {
	node.content = [
		{
			type: 'paragraph',
			content: [
				{
					type: 'placeholder',
					attrs: {
						text: placeholderText,
					},
				},
			],
		},
	];
};

export const insertContentPropertiesMacroPlaceholder = (
	node: any,
	keyPlaceholderHeading: string,
	valuePlaceholderHeading: string,
): void => {
	node.content = [
		{
			type: 'table',
			attrs: {
				isNumberColumnEnabled: false,
				layout: 'default',
				localId: getMacroId(null),
				width: 760,
			},
			content: [
				{
					type: 'tableRow',
					content: [
						{
							type: 'tableHeader',
							attrs: {},
							content: [
								{
									type: 'paragraph',
									content: [
										{
											type: 'placeholder',
											attrs: {
												text: keyPlaceholderHeading,
											},
										},
									],
								},
							],
						},
						{
							type: 'tableCell',
							attrs: {
								background: '#ffffff',
							},
							content: [
								{
									type: 'paragraph',
									content: [
										{
											type: 'placeholder',
											attrs: {
												text: valuePlaceholderHeading,
											},
										},
									],
								},
							],
						},
					],
				},
				{
					type: 'tableRow',
					content: [
						{
							type: 'tableHeader',
							attrs: {
								background: '#f4f5f7',
							},
							content: [
								{
									type: 'paragraph',
									content: [],
								},
							],
						},
						{
							type: 'tableCell',
							attrs: {},
							content: [
								{
									type: 'paragraph',
									content: [],
								},
							],
						},
					],
				},
				{
					type: 'tableRow',
					content: [
						{
							type: 'tableHeader',
							attrs: {
								background: '#f4f5f7',
							},
							content: [
								{
									type: 'paragraph',
									content: [],
								},
							],
						},
						{
							type: 'tableCell',
							attrs: {},
							content: [
								{
									type: 'paragraph',
									content: [],
								},
							],
						},
					],
				},
			],
		},
	];
};

const getMatchingAppLinkForViewPage = async (selectedServerId: string) => {
	try {
		const res = await cfetch('/wiki/rest/jiraanywhere/1.0/servers');
		const appLinks: { url: string; rpcUrl: string; id: string }[] = await res.json();

		return appLinks.find((appLink) => appLink.id === selectedServerId);
	} catch {}
};

const isCloudAppLinkFromUrl = (url?: string) => {
	if (!url) {
		return;
	}

	return url.includes('atlassian.net') || url.includes('jira-dev.com');
};

export const getJiraHostingOption = async (
	macroName: string,
	selectedServerId: string = '',
	isViewPage: boolean = false,
): Promise<'unknown' | 'cloud' | 'server' | undefined> => {
	if (macroName !== 'jira') {
		return;
	}

	let isCloudAppLink: boolean | undefined;

	if (isViewPage) {
		const selectedServerObject = await getMatchingAppLinkForViewPage(selectedServerId);

		isCloudAppLink =
			isCloudAppLinkFromUrl(selectedServerObject?.url) ||
			isCloudAppLinkFromUrl(selectedServerObject?.rpcUrl);
	} else {
		try {
			// module is only available on edit page
			const ApplinksBridge = window.require('confluence/jim/jira/applinks-bridge-for-spa');

			isCloudAppLink = await ApplinksBridge.isCloudAppLinkFromServerId(selectedServerId);
		} catch {}
	}

	if (isCloudAppLink === undefined) {
		return 'unknown';
	}

	return isCloudAppLink ? 'cloud' : 'server';
};

// This is not an extensive list, as some macros are added elsewhere (ex. Smart button)
export const MACROS_TO_HIDE_COMPANY_HUB: Set<string> = new Set([
	'anchor',
	'attachments',
	'calendar',
	'children',
	'contentbylabel',
	'content-report-table',
	'create-from-template',
	'decisionreport',
	'detailssummary',
	'gadget',
	'excerpt',
	'excerpt-include',
	'expand',
	'iframe',
	'include',
	'jira',
	'listlabels',
	'pagetree',
	'pagetreesearch',
	'panel',
	'popular-labels',
	'profile',
	'recently-updated-dashboard',
	'roadmap',
	'spaces',
	'tasks-report-macro',
	'toc',
	'toc-zone',
	'userlister',
	'viewpdf',
	'viewxls',
]);

export const filterOutHiddenMacros = (
	macros: any,
	hideMacrosOptions?: { isLivePage?: boolean; isCompanyHub?: boolean },
): any[] => {
	const newMacros: any[] = [];
	const macrosToHideLivePage = new Set(
		StatsigConfigurations.getStringList('confluence_frontend_hide_selected_macros', []),
	);

	for (const macro of macros) {
		const hideCompanyHubMacro =
			hideMacrosOptions?.isCompanyHub && MACROS_TO_HIDE_COMPANY_HUB.has(macro?.macroName);
		const hideLivePageMacro =
			hideMacrosOptions?.isLivePage && macrosToHideLivePage.has(macro?.macroName);

		if (hideCompanyHubMacro || hideLivePageMacro) {
			const hiddenMacro = { ...macro, hidden: true };
			newMacros.push(hiddenMacro);
		} else {
			newMacros.push(macro);
		}
	}
	return newMacros;
};
